import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { initAuth, getAccessToken, loginWithMicrosoft, logout } from "../auth/authService";
import {
  EmailContext,
  AnalyseResponse,
  GenerateEmailRequest,
  GeneratedEmailResponse,
  HistoryItem,
  PanelView,
  WizardAnswers,
  ProgressStep,
  ProgressStepKey,
  ProgressStepStatus,
  ThreadMessage,
} from "../types";
import { getHistoryDetail, runToAnalyseResponse, runToGeneratedEmail } from "./history";
import { MOCK_EMAIL_CONTEXT } from "../mocks/emailContext";
import { MOCK_ANALYSE_RESPONSE } from "../mocks/analyseResponse";
import LoginView from "./views/LoginView";
import HomeView from "./views/HomeView";
import WizardView from "./views/WizardView";
import ManualEntryView from "./views/ManualEntryView";
import WriteEmailView from "./views/WriteEmailView";
import LoadingView from "./views/LoadingView";
import ResultsView from "./views/ResultsView";
import WriteEmailResultsView from "./views/WriteEmailResultsView";
import { streamAnalyse, streamGenerate } from "./streamAnalyse";
import { BACKEND_URL } from "../config";
import { saveUiState, loadUiState, clearUiState } from "./sessionState";
import { getMailboxContext, onContextChange, initMailboxContext } from "../office/mailboxContext";
import { isComposeMode, applyRewriteToBody } from "../office/composeBridge";
import { initThemeWatcher } from "../office/theme";
import "../styles/tokens.css";

const GRAPH = "https://graph.microsoft.com/v1.0";
const THREAD_SELECT = "body,from,receivedDateTime,id,isDraft";

const isDev =
  typeof window !== "undefined" &&
  window.location.protocol === "http:" &&
  window.location.hostname === "localhost";

// True when running inside an Outlook task pane. Evaluated lazily because
// Office.context is only populated after Office.onReady fires — a module-level
// constant would always read undefined and force the mock branch.
function isOffice(): boolean {
  return (
    typeof Office !== "undefined" &&
    !!Office.context &&
    !!Office.context.mailbox
  );
}

// ── Graph thread enrichment ──────────────────────────────────────────────────

type GraphMsg = Record<string, unknown>;

function addrOf(m: GraphMsg) {
  return (m.from as { emailAddress?: { name?: string; address?: string } })
    ?.emailAddress ?? {};
}

function htmlToText(html: string): string {
  try {
    return (new DOMParser().parseFromString(html, "text/html").body.textContent ?? "")
      .replace(/\s+/g, " ")
      .trim();
  } catch {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
}

async function fetchThreadMessages(
  token: string,
  conversationId: string,
): Promise<GraphMsg[]> {
  const headers = { Authorization: `Bearer ${token}` };
  const params = new URLSearchParams({
    "$filter": `conversationId eq '${conversationId}'`,
    "$top": "50",
    "$select": THREAD_SELECT,
  });
  try {
    const res = await fetch(`${GRAPH}/me/messages?${params}`, { headers });
    if (!res.ok) return [];
    const data = (await res.json()) as { value?: GraphMsg[] };
    return (data.value ?? []).filter((m) => !m.isDraft);
  } catch {
    return [];
  }
}

async function enrichWithThread(ctx: EmailContext): Promise<EmailContext> {
  if (!ctx.conversationId) return ctx;
  let token: string;
  try {
    token = await getAccessToken();
  } catch {
    return ctx;
  }
  const msgs = await fetchThreadMessages(token, ctx.conversationId);
  // A reply to a single prior email is still a thread — attach it. Only bail when
  // the fetch returned nothing at all (otherwise we'd wipe the draft body below).
  if (msgs.length === 0) return ctx;

  const sorted = [...msgs].sort(
    (a, b) =>
      new Date((a.receivedDateTime as string) ?? 0).getTime() -
      new Date((b.receivedDateTime as string) ?? 0).getTime(),
  );

  const threadMessages: ThreadMessage[] = sorted.map((m) => {
    const a = addrOf(m);
    return {
      sender: a.name ?? "",
      senderEmail: a.address ?? "",
      body: ((m.body as { content?: string }) ?? {}).content ?? "",
      receivedAt: (m.receivedDateTime as string) ?? "",
    };
  });

  const combinedBody = threadMessages
    .map((m, i) => `[Message ${i + 1} — ${m.sender || m.senderEmail}]\n${htmlToText(m.body)}`)
    .join("\n\n---\n\n");

  return {
    ...ctx,
    body: ctx.hasDraft ? combinedBody : ctx.body,
    threadLength: threadMessages.length,
    messages: threadMessages,
    isReply: ctx.hasDraft && threadMessages.length > 0,
  };
}

async function loadEmailContext(): Promise<EmailContext | null> {
  if (isDev || !isOffice()) {
    return {
      ...MOCK_EMAIL_CONTEXT,
      hasDraft: true,
      isReply: false,
      draftBody: MOCK_EMAIL_CONTEXT.body,
      draftSubject: MOCK_EMAIL_CONTEXT.subject,
    };
  }
  const base = await getMailboxContext();
  if (!base) return null;
  return enrichWithThread(base);
}

// ── Progress steps ──────────────────────────────────────────────────────────

const STEP_LABELS: Record<ProgressStepKey, string> = {
  reading: "Reading your email",
  applyingContext: "Understanding your objectives",
  scoring: "Evaluating Trusted Partner behaviors",
  coaching: "Identifying coaching opportunities",
  rewrite: "Creating your improved email",
  drafting: "Creating your email",
  scoringDraft: "Calculating the ASLAN+ score",
};

// Analyse flow (/api/analyse/).
const STEP_ORDER: ProgressStepKey[] = [
  "reading",
  "applyingContext",
  "scoring",
  "coaching",
  "rewrite",
];

// Write-email flow (/api/generate-email/). Mirrors the keys the backend emits.
const GENERATE_STEP_ORDER: ProgressStepKey[] = [
  "reading",
  "applyingContext",
  "drafting",
  "scoringDraft",
];

function stepsFor(order: ProgressStepKey[]): ProgressStep[] {
  return order.map((key) => ({ key, label: STEP_LABELS[key], status: "pending" }));
}

function initialSteps(): ProgressStep[] {
  return stepsFor(STEP_ORDER);
}

function initialGenerateSteps(): ProgressStep[] {
  return stepsFor(GENERATE_STEP_ORDER);
}

type StepFn = (key: ProgressStepKey, status: ProgressStepStatus, durationSec?: number) => void;

async function simulateStream(
  order: ProgressStepKey[],
  timings: Partial<Record<ProgressStepKey, number>>,
  onStep: StepFn,
): Promise<void> {
  for (const key of order) {
    const dur = timings[key] ?? 0.5;
    onStep(key, "active");
    await new Promise((r) => setTimeout(r, dur * 1000));
    onStep(key, "done", dur);
  }
}

async function simulateMockStream(onStep: StepFn): Promise<AnalyseResponse> {
  await simulateStream(
    STEP_ORDER,
    { reading: 0.3, applyingContext: 0.6, scoring: 1.2, coaching: 0.9, rewrite: 1.0 },
    onStep,
  );
  return MOCK_ANALYSE_RESPONSE;
}

async function simulateMockGenerateStream(onStep: StepFn): Promise<void> {
  await simulateStream(
    GENERATE_STEP_ORDER,
    { reading: 0.3, applyingContext: 0.6, drafting: 1.4, scoringDraft: 1.0 },
    onStep,
  );
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const [view, setView] = useState<PanelView>("login");
  const [isInitializing, setIsInitializing] = useState(true);
  const [emailContext, setEmailContext] = useState<EmailContext | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalyseResponse | null>(null);
  // Snapshot of the draft body+subject taken at the moment analysis starts.
  // The results panel renders this instead of the live emailContext so pasting
  // the rewrite back into the draft doesn't cause the "Original" view to flip
  // to the rewritten text (which would also wipe the diff highlights).
  const [analysisSnapshot, setAnalysisSnapshot] = useState<{ body: string; subject: string } | null>(null);
  const [analyseError, setAnalyseError] = useState<string | null>(null);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>(initialSteps);
  const [manualEmailData, setManualEmailData] = useState<{ body: string; subject: string } | null>(null);
  const [writeEmailBrief, setWriteEmailBrief] = useState<string>("");
  const [writeEmailWizard, setWriteEmailWizard] = useState<WizardAnswers | null>(null);
  const [writeEmailResult, setWriteEmailResult] = useState<GeneratedEmailResponse | null>(null);
  // True when the current result/writeEmailResults view was opened from a stored
  // history item rather than a fresh run. It is not tied to the open draft, so
  // "Back" returns home and the rewrite can't be inserted into the live compose.
  const [resultFromHistory, setResultFromHistory] = useState(false);
  // Sender/recipient captured from a re-opened history item, so the results
  // screen's coaching-detail lookups use the original email's parties.
  const [historyMeta, setHistoryMeta] = useState<{ senderName?: string; recipientName?: string } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const lastMessageIdRef = useRef<string | null>(null);
  // The Outlook messageId the current result belongs to, so persisted state is
  // restored against the right email after a reload. null for manual-entry
  // analyses (not tied to the open message).
  const resultMessageIdRef = useRef<string | null>(null);
  const refreshingRef = useRef(false);
  const pendingRefreshRef = useRef(false);

  // Latest-wins refresh: if a refresh is already running and a new request
  // comes in (e.g. user switched emails again), queue exactly one re-run.
  const refreshContext = async () => {
    if (refreshingRef.current) {
      pendingRefreshRef.current = true;
      return;
    }
    refreshingRef.current = true;
    try {
      do {
        pendingRefreshRef.current = false;
        const ctx = await loadEmailContext();
        lastMessageIdRef.current = ctx?.messageId ?? null;
        setEmailContext(ctx);
      } while (pendingRefreshRef.current);
    } finally {
      refreshingRef.current = false;
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        initThemeWatcher();
        if (isDev || !isOffice()) {
          setEmailContext(await loadEmailContext());
          setView("home");
        } else {
          initMailboxContext();
          const account = await initAuth();
          if (account) {
            const ctx = await loadEmailContext();
            setEmailContext(ctx);
            lastMessageIdRef.current = ctx?.messageId ?? null;
            // Restore the view/result the user was on before a reload (pin
            // toggle or message switch in OWA both reload the iframe), but only
            // when the same message is still selected.
            const restored = loadUiState(ctx?.messageId ?? null);
            if (restored) {
              resultMessageIdRef.current = restored.messageId;
              if (restored.analysisResult) setAnalysisResult(restored.analysisResult);
              if (restored.analysisSnapshot) setAnalysisSnapshot(restored.analysisSnapshot);
              if (restored.writeEmailResult) setWriteEmailResult(restored.writeEmailResult);
              setView(restored.view);
            } else {
              setView("home");
            }
          } else {
            setView("login");
          }
        }
      } finally {
        setIsInitializing(false);
      }
    };
    init();
  }, []);

  // Persist the current view + results so a reload (OWA pin toggle / message
  // switch) can restore them. Keyed to resultMessageIdRef — the message the
  // result actually belongs to — so it never restores against a different
  // email. Non-result views clear the stash (handled in saveUiState).
  useEffect(() => {
    if (isInitializing) return;
    void saveUiState({
      view,
      messageId: resultMessageIdRef.current,
      analysisResult,
      analysisSnapshot,
      writeEmailResult,
    });
  }, [view, analysisResult, analysisSnapshot, writeEmailResult, isInitializing]);

  // Subscribe to mailbox-context changes from Office.js. Replaces the
  // chrome.runtime.onMessage(EMAIL_CONTEXT_UPDATE) listener.
  useEffect(() => {
    if (isDev || !isOffice()) return;
    const unsubscribe = onContextChange((incoming) => {
      if (!incoming) {
        lastMessageIdRef.current = null;
        void refreshContext();
        return;
      }
      // messageId changed → user switched emails → full reload (with Graph thread)
      if (incoming.messageId !== lastMessageIdRef.current) {
        lastMessageIdRef.current = incoming.messageId;
        void refreshContext();
        return;
      }
      // Same message → merge only draft fields
      setEmailContext((prev) =>
        prev
          ? {
              ...prev,
              isCompose: incoming.isCompose,
              hasDraft: incoming.hasDraft,
              isReply: incoming.isReply,
              draftSubject: incoming.draftSubject,
              draftBody: incoming.draftBody,
            }
          : incoming,
      );
    });
    return unsubscribe;
  }, []);

  // Refresh when the task pane becomes visible (rarely fires inside Office,
  // but harmless and matches the Chrome extension behaviour).
  useEffect(() => {
    if (isDev || !isOffice()) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void refreshContext();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  const handleLogin = async () => {
    await loginWithMicrosoft();
    setEmailContext(await loadEmailContext());
    setView("home");
  };

  const handleLogout = async () => {
    await logout();
    await clearUiState();
    resultMessageIdRef.current = null;
    setEmailContext(null);
    setAnalysisResult(null);
    setView("login");
  };

  const updateStep = (
    key: ProgressStepKey,
    status: ProgressStepStatus,
    durationSec?: number,
  ) => {
    setProgressSteps((prev) =>
      prev.map((s) => (s.key === key ? { ...s, status, durationSec } : s)),
    );
  };

  const handleStartAnalyse = () => {
    if (!emailContext) return;
    setManualEmailData(null);
    setAnalyseError(null);
    setView("wizard");
  };

  const handleWizardSubmit = async (answers: WizardAnswers) => {
    const isManual = manualEmailData !== null;
    if (!isManual && !emailContext) return;
    // Manual-entry results aren't tied to the open message, so don't key them
    // to a messageId (they won't be restored against a real email on reload).
    resultMessageIdRef.current = isManual ? null : (emailContext?.messageId ?? null);
    setResultFromHistory(false);
    setHistoryMeta(null);
    setAnalyseError(null);
    setProgressSteps(initialSteps());
    setView("loading");

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    // Freeze the inputs that the results panel needs to keep showing even if
    // the user pastes the rewrite back into the draft (which would otherwise
    // mutate emailContext.draftBody via the 1.2s poller).
    const snapshotBody = isManual
      ? manualEmailData!.body
      : (emailContext!.draftBody ?? emailContext!.body);
    const snapshotSubject = isManual
      ? manualEmailData!.subject
      : (emailContext!.draftSubject || emailContext!.subject);
    setAnalysisSnapshot({ body: snapshotBody, subject: snapshotSubject });

    try {
      let result: AnalyseResponse;
      if (isDev) {
        result = await simulateMockStream(updateStep);
      } else {
        const req = {
          messageId: isManual ? `manual-${Date.now()}` : (emailContext!.messageId ?? "unknown"),
          subject: snapshotSubject,
          body: snapshotBody,
          from: emailContext?.senderEmail ?? "",
          senderName: emailContext?.sender ?? "",
          recipientName: emailContext?.recipientName ?? "",
          receivedAt: new Date().toISOString(),
          wizard: answers,
          threadContext: isManual ? undefined : (emailContext!.isReply ? emailContext!.body : undefined),
          isReply: isManual ? false : emailContext!.isReply,
        };
        result = await streamAnalyse(
          "/api/analyse/",
          req,
          updateStep,
          ctrl.signal,
        );
      }
      setAnalysisResult(result);
      setView("results");
    } catch (err: unknown) {
      if ((err as Error)?.name === "AbortError") return;
      const msg = err instanceof Error ? err.message : "Analysis failed";
      setAnalyseError(
        `Could not reach the AI backend. Make sure it's running at ${BACKEND_URL}.\n${msg}`,
      );
    }
  };

  const handleAnalyseRetry = () => {
    setView("wizard");
  };

  const handleClose = () => {
    abortRef.current?.abort();
    // Office task panes can't be closed via window.close. Reset to home.
    if (isOffice()) {
      setView("home");
      return;
    }
    window.close();
  };

  const handleBackToHome = () => {
    abortRef.current?.abort();
    setAnalysisResult(null);
    setAnalysisSnapshot(null);
    setAnalyseError(null);
    setManualEmailData(null);
    setResultFromHistory(false);
    setHistoryMeta(null);
    setView("home");
  };

  // Re-open a stored run from the "Your recent scores" list. Fetches the full
  // run and rebuilds the same result screen the user originally saw — analyse
  // runs open ResultsView, generate runs open WriteEmailResultsView. The result
  // isn't tied to the open draft, so it's flagged as history (Back → home, no
  // insert-into-draft). Errors propagate to the row, which shows a retry hint.
  const handleOpenHistory = async (item: HistoryItem) => {
    const detail = await getHistoryDetail(item.id);
    abortRef.current?.abort();
    resultMessageIdRef.current = null;
    setAnalyseError(null);
    setResultFromHistory(true);

    if (detail.type === "generate") {
      setWriteEmailResult(runToGeneratedEmail(detail));
      setView("writeEmailResults");
      return;
    }

    setAnalysisResult(runToAnalyseResponse(detail));
    setAnalysisSnapshot({ body: detail.originalBody ?? "", subject: detail.subject ?? "" });
    setHistoryMeta({
      senderName: detail.senderName ?? undefined,
      recipientName: detail.recipientName ?? undefined,
    });
    setView("results");
  };

  // Paste the rewrite into the active Outlook compose draft. Replaces the
  // user's drafted message but keeps any quoted reply/forward history below it
  // (applyRewriteToBody handles the splice + the compose-mode guard). Returns
  // true on success so the button can show a confirmation state.
  const handleApplyToDraft = async (text: string): Promise<boolean> => {
    return applyRewriteToBody(text);
  };

  const handleManualEntrySubmit = (body: string, subject: string) => {
    setManualEmailData({ body, subject });
    setAnalyseError(null);
    setView("wizard");
  };

  const handleRewrite = () => {
    setAnalyseError(null);
    setView("manualEntry");
  };

  const handleWrite = () => {
    setAnalyseError(null);
    setWriteEmailBrief("");
    setWriteEmailWizard(null);
    setWriteEmailResult(null);
    setView("writeEmail");
  };

  // Shared driver for the "Write email for me" flow. Streams /api/generate-email/
  // via streamGenerate (which goes through apiFetch, so the auth header is always
  // attached) and drives the loading-screen steps from the backend's NDJSON events.
  // onErrorView is where a failure lands the user (the form for a fresh submit, the
  // results screen for a retry).
  const runGenerate = async (
    wizard: WizardAnswers | null,
    brief: string,
    onErrorView: PanelView,
  ) => {
    if (!emailContext) return;
    resultMessageIdRef.current = emailContext.messageId ?? null;
    setResultFromHistory(false);
    setWriteEmailBrief(brief);
    setWriteEmailWizard(wizard);
    setAnalyseError(null);
    setProgressSteps(initialGenerateSteps());
    setView("loading");

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      let result: GeneratedEmailResponse;
      if (isDev) {
        await simulateMockGenerateStream(updateStep);
        result = {
          body: "Hi Sarah,\n\nWith Thursday's call confirmed — I'll send a short agenda ahead of time so we make the most of the time together.\n\nOne thing I'd love to understand going in: as you evaluate your options, what's the single biggest factor guiding your decision?\n\nLooking forward to it.\nJames",
          score: 84,
          verdict: "good",
          caption: "Generated using AI methodology",
        };
      } else {
        // The reply is written AS the logged-in user, addressed to the other
        // party. In read mode `sender`/`senderEmail` hold the original email's
        // sender (the person we're replying to), so they become the recipient;
        // in compose mode `sender` already holds the logged-in user and
        // `recipientName` already holds the To field, so use that as-is.
        const isReplyToSender = !emailContext.isCompose;
        const req: GenerateEmailRequest = {
          threadContext: emailContext.body,
          brief,
          from: emailContext.userEmail ?? "",
          senderName: emailContext.userName ?? "",
          recipientName: isReplyToSender
            ? (emailContext.sender ?? "")
            : (emailContext.recipientName ?? ""),
          wizard: wizard ?? undefined,
        };
        result = await streamGenerate("/api/generate-email/", req, updateStep, ctrl.signal);
      }
      setWriteEmailResult(result);
      setView("writeEmailResults");
    } catch (err: unknown) {
      if ((err as Error)?.name === "AbortError") return;
      const msg = err instanceof Error ? err.message : "Generation failed";
      setAnalyseError(
        `Could not reach the AI backend. Make sure it's running at ${BACKEND_URL}.\n${msg}`,
      );
      setView(onErrorView);
    }
  };

  const handleGenerateEmail = (wizard: WizardAnswers, brief: string) =>
    runGenerate(wizard, brief, "writeEmail");

  // Fade out the HTML-injected splash once the first real UI is about to paint.
  // The splash itself lives in taskpane.html so it can appear before the JS
  // bundle has finished loading.
  useLayoutEffect(() => {
    if (isInitializing) return;
    const splash = document.getElementById("splash");
    if (!splash) return;
    splash.classList.add("hidden");
    const handle = window.setTimeout(() => {
      splash.parentNode?.removeChild(splash);
    }, 260);
    return () => window.clearTimeout(handle);
  }, [isInitializing]);

  if (isInitializing) {
    return null;
  }

  if (view === "login") return <LoginView onLogin={handleLogin} />;

  if (view === "writeEmail") {
    return (
      <WriteEmailView
        emailContext={emailContext}
        initialWizard={writeEmailWizard}
        initialBrief={writeEmailBrief}
        onGenerate={handleGenerateEmail}
        onClose={handleClose}
        onBack={handleBackToHome}
      />
    );
  }

  if (view === "writeEmailResults" && writeEmailResult) {
    return (
      <WriteEmailResultsView
        result={writeEmailResult}
        onBack={handleBackToHome}
        onClose={handleClose}
        onApplyToDraft={handleApplyToDraft}
        canApplyToDraft={isOffice() && isComposeMode()}
        senderName={emailContext?.userName}
        recipientName={emailContext?.recipientName ?? emailContext?.sender}
        threadContext={emailContext?.body}
        wizard={writeEmailWizard}
      />
    );
  }

  if (view === "manualEntry") {
    return (
      <ManualEntryView
        initialBody={manualEmailData?.body ?? ""}
        initialSubject={manualEmailData?.subject ?? ""}
        onAnalyse={handleManualEntrySubmit}
        onClose={handleClose}
        onBack={handleBackToHome}
      />
    );
  }

  if (view === "wizard") {
    return (
      <WizardView
        onAnalyse={handleWizardSubmit}
        onClose={handleClose}
        onBack={handleBackToHome}
      />
    );
  }

  if (view === "loading") {
    return (
      <LoadingView
        steps={progressSteps}
        error={analyseError}
        onRetry={analyseError ? handleAnalyseRetry : undefined}
        onClose={handleClose}
      />
    );
  }

  if (view === "results" && analysisResult) {
    return (
      <ResultsView
        result={analysisResult}
        subject={analysisSnapshot?.subject ?? emailContext?.subject ?? ""}
        originalDraft={analysisSnapshot?.body ?? manualEmailData?.body ?? emailContext?.draftBody}
        threadContext={resultFromHistory ? undefined : (emailContext?.isReply ? emailContext?.body : undefined)}
        senderName={resultFromHistory ? historyMeta?.senderName : emailContext?.sender}
        recipientName={resultFromHistory ? historyMeta?.recipientName : emailContext?.recipientName}
        onBack={resultFromHistory ? handleBackToHome : () => setView("wizard")}
        onClose={handleClose}
        onApplyToDraft={handleApplyToDraft}
        canApplyToDraft={isOffice() && isComposeMode()}
      />
    );
  }

  return (
    <HomeView
      emailContext={emailContext}
      onAnalyse={handleStartAnalyse}
      onRewrite={handleRewrite}
      onWrite={handleWrite}
      onClose={handleClose}
      onLogout={handleLogout}
      onRefresh={refreshContext}
      onOpenHistory={handleOpenHistory}
    />
  );
}
